// AuthGate — wraps the whole app. Asks the API who is signed in, and shows the
// sign-in screen instead of the app when nobody is.
//
// The session lives in a cookie set by the API on health-api.tallyio.com.au.
// The SPA is on health.tallyio.com.au — a different origin but the same site,
// which is why `credentials: "include"` is enough and no token is ever handled
// by JavaScript.

import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { apiBase, getSession, onSessionLost, type SessionUser } from "../lib/api";

type State =
  | { status: "checking" }
  | { status: "in"; user: SessionUser }
  | { status: "out"; reason?: string };

const SessionContext = createContext<SessionUser | null>(null);

/**
 * The signed-in user. Available anywhere inside the gate, so the shell does not
 * have to ask the API again for something it already established.
 */
export function useSession(): SessionUser {
  const user = useContext(SessionContext);
  if (!user) throw new Error("useSession must be used inside <AuthGate>");
  return user;
}

export function AuthGate({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<State>({ status: "checking" });

  const check = useCallback(async () => {
    try {
      const { user } = await getSession();
      setState({ status: "in", user });
    } catch (e) {
      const reason =
        e instanceof Error && e.message === "forbidden"
          ? "That Google account is not the one this tracker belongs to."
          : undefined;
      setState({ status: "out", reason });
    }
  }, []);

  useEffect(() => {
    void check();
  }, [check]);

  // A session that lapses mid-use shows the sign-in screen rather than letting
  // every page fail on its own.
  useEffect(
    () => onSessionLost(() => setState({ status: "out" })),
    []
  );

  if (state.status === "checking") {
    return (
      <div className="grid min-h-dvh place-items-center bg-canvas">
        <p className="caption" aria-live="polite">
          Checking your session…
        </p>
      </div>
    );
  }

  if (state.status === "out") return <SignIn reason={state.reason} />;

  return (
    <SessionContext.Provider value={state.user}>{children}</SessionContext.Provider>
  );
}

function SignIn({ reason }: { reason?: string }) {
  return (
    <div className="grid min-h-dvh place-items-center bg-canvas px-4">
      <main className="card-pad w-full max-w-sm text-center">
        <p className="eyebrow">Health</p>
        <h1 className="page-title mt-1">Sign in</h1>
        <p className="muted mt-3 text-small">
          {reason ??
            "This tracker is private. Sign in with the Google account it belongs to."}
        </p>

        {/* A plain link, not fetch: the OAuth flow is a series of top-level
            navigations, and the API sets the state cookie on the way out. */}
        <a
          className="btn-accent mt-6 w-full"
          href={`${apiBase}/auth/login`}
        >
          <GoogleMark />
          Continue with Google
        </a>

        <p className="caption mt-6">No password. One account.</p>
      </main>
    </div>
  );
}

function GoogleMark() {
  return (
    <svg width="16" height="16" viewBox="0 0 18 18" aria-hidden="true">
      <path
        fill="currentColor"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62Z"
        opacity=".9"
      />
      <path
        fill="currentColor"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18Z"
        opacity=".7"
      />
      <path
        fill="currentColor"
        d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33Z"
        opacity=".5"
      />
      <path
        fill="currentColor"
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58Z"
      />
    </svg>
  );
}
